<script>
import { mapGetters } from 'vuex';
import ReportsAPI from 'dashboard/api/reports';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Spinner from 'shared/components/Spinner.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';

// The backend enum is the source of truth for conversation states; this list
// only guards the i18n lookup so an unknown/future state still renders its raw
// value instead of an empty label (mirrors useDebtStateFeedback).
const KNOWN_STATES = ['ESTAGNADO', 'SRETORNO', 'CONCLUIDA', 'LIXO'];

// Maps the categorical confidence band emitted by the classifier to the shared
// CONVERSATION.DEBT_STATE_WIDGET.CONFIDENCE i18n keys. 'null' (COALESCE on the
// backend) and any unknown band fall back to a dedicated label.
const BAND_I18N_KEY = {
  alta: 'HIGH',
  media: 'MEDIUM',
  baixa: 'LOW',
};

const PRECISION_BAR_COLOR = 'rgb(31, 147, 255)';

export default {
  name: 'DebtStateGovernance',
  components: {
    Banner,
    Spinner,
    BarChart,
    ReportHeader,
    ReportMetricCard,
  },
  data() {
    return {
      metrics: null,
      isLoading: false,
      hasError: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    total() {
      return this.metrics?.total ?? {};
    },
    readiness() {
      return this.metrics?.readiness ?? {};
    },
    isEmpty() {
      return !this.total.feedbacks;
    },
    locked() {
      return this.metrics?.locked ?? 0;
    },
    precisionRows() {
      return this.metrics?.precision_by_predicted ?? [];
    },
    confusionRows() {
      return this.metrics?.confusion ?? [];
    },
    calibrationRows() {
      return this.metrics?.calibration ?? [];
    },
    adoptionRows() {
      return this.metrics?.adoption ?? [];
    },
    readinessValue() {
      const min = this.readiness.min_class_gold ?? 0;
      const threshold = this.readiness.threshold ?? 0;
      return `${min} / ${threshold}`;
    },
    readinessReadyLabel() {
      return this.readiness.ready
        ? this.$t('DEBT_STATE_GOVERNANCE.CARDS.READINESS.READY_YES')
        : this.$t('DEBT_STATE_GOVERNANCE.CARDS.READINESS.READY_NO');
    },
    precisionCollection() {
      return {
        labels: this.precisionRows.map(row =>
          this.stateLabel(row.predicted_state)
        ),
        datasets: [
          {
            label: this.$t('DEBT_STATE_GOVERNANCE.PRECISION.AXIS_Y'),
            backgroundColor: PRECISION_BAR_COLOR,
            data: this.precisionRows.map(row => row.pct_ok),
          },
        ],
      };
    },
    precisionChartOptions() {
      const rows = this.precisionRows;
      return {
        scales: {
          x: {
            grid: { drawOnChartArea: false },
          },
          y: {
            beginAtZero: true,
            max: 100,
            ticks: { stepSize: 20 },
            grid: { drawOnChartArea: false },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.parsed.y}%`,
              afterLabel: ctx => {
                const row = rows[ctx.dataIndex] || {};
                return [
                  this.$t('DEBT_STATE_GOVERNANCE.PRECISION.TOOLTIP_TOTAL', {
                    total: row.total ?? 0,
                  }),
                  this.$t('DEBT_STATE_GOVERNANCE.PRECISION.TOOLTIP_BIAS'),
                ];
              },
            },
          },
        },
      };
    },
  },
  watch: {
    accountId() {
      this.fetchMetrics();
    },
  },
  mounted() {
    this.fetchMetrics();
  },
  methods: {
    async fetchMetrics() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const { data } = await ReportsAPI.getDebtStateMetrics();
        this.metrics = data;
      } catch (error) {
        this.hasError = true;
        this.metrics = null;
      } finally {
        this.isLoading = false;
      }
    },
    formatNumber(value) {
      return Number(value ?? 0).toLocaleString();
    },
    formatPercent(value) {
      return `${Number(value ?? 0)}%`;
    },
    stateLabel(state) {
      if (KNOWN_STATES.includes(state)) {
        return this.$t(`CONVERSATION.DEBT_STATE_WIDGET.STATES.${state}`);
      }
      return state || '--';
    },
    bandLabel(band) {
      const key = BAND_I18N_KEY[band];
      if (key) {
        return this.$t(`CONVERSATION.DEBT_STATE_WIDGET.CONFIDENCE.${key}`);
      }
      return this.$t('DEBT_STATE_GOVERNANCE.CALIBRATION.BAND_NULL');
    },
  },
};
</script>

<template>
  <ReportHeader
    :header-title="$t('DEBT_STATE_GOVERNANCE.HEADER')"
    :header-description="$t('DEBT_STATE_GOVERNANCE.DESCRIPTION')"
  />

  <div
    v-if="isLoading"
    class="flex items-center justify-center gap-2 py-16 text-sm text-n-slate-11"
  >
    <Spinner />
    <span>{{ $t('DEBT_STATE_GOVERNANCE.LOADING') }}</span>
  </div>

  <div
    v-else-if="hasError"
    class="flex items-center justify-center py-16 text-sm text-n-ruby-11"
  >
    {{ $t('DEBT_STATE_GOVERNANCE.ERROR') }}
  </div>

  <div v-else class="flex flex-col gap-6 pb-6">
    <!-- Bias notice: precision here is CONDITIONAL, never global accuracy. -->
    <Banner color="amber">
      <div class="flex flex-col gap-1">
        <span class="font-semibold">
          {{ $t('DEBT_STATE_GOVERNANCE.BIAS_NOTICE.TITLE') }}
        </span>
        <span>{{ $t('DEBT_STATE_GOVERNANCE.BIAS_NOTICE.BODY') }}</span>
      </div>
    </Banner>

    <div
      v-if="isEmpty"
      class="flex items-center justify-center py-16 text-sm text-center text-n-slate-11"
    >
      {{ $t('DEBT_STATE_GOVERNANCE.EMPTY') }}
    </div>

    <template v-else>
      <!-- Metric cards -->
      <div
        class="grid grid-cols-2 gap-x-6 gap-y-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5 md:grid-cols-3 lg:grid-cols-4"
      >
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.FEEDBACKS.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.FEEDBACKS.TOOLTIP')"
          :value="formatNumber(total.feedbacks)"
        />
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.CORRECTIONS.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.CORRECTIONS.TOOLTIP')"
          :value="formatNumber(total.corrections)"
        />
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.CONFIRMATIONS.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.CONFIRMATIONS.TOOLTIP')"
          :value="formatNumber(total.confirmations)"
        />
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.AGENTS.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.AGENTS.TOOLTIP')"
          :value="formatNumber(total.agents)"
        />
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.CONVERSATIONS.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.CONVERSATIONS.TOOLTIP')"
          :value="formatNumber(total.conversations)"
        />
        <ReportMetricCard
          :label="$t('DEBT_STATE_GOVERNANCE.CARDS.LOCKED.LABEL')"
          :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.LOCKED.TOOLTIP')"
          :value="formatNumber(locked)"
        />
        <div>
          <ReportMetricCard
            :label="$t('DEBT_STATE_GOVERNANCE.CARDS.READINESS.LABEL')"
            :info-text="$t('DEBT_STATE_GOVERNANCE.CARDS.READINESS.TOOLTIP')"
            :value="readinessValue"
          />
          <span
            class="inline-block mt-1 text-sm font-medium"
            :class="readiness.ready ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ readinessReadyLabel }}
          </span>
        </div>
      </div>

      <!-- Conditional precision by predicted state -->
      <div
        class="flex flex-col gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <h3 class="m-0 text-base font-medium text-n-slate-12">
          {{ $t('DEBT_STATE_GOVERNANCE.PRECISION.TITLE') }}
        </h3>
        <div v-if="precisionRows.length" class="h-72">
          <BarChart
            :collection="precisionCollection"
            :chart-options="precisionChartOptions"
          />
        </div>
        <p v-else class="m-0 text-sm text-n-slate-11">
          {{ $t('DEBT_STATE_GOVERNANCE.PRECISION.EMPTY') }}
        </p>
      </div>

      <!-- Confusion matrix -->
      <div
        class="flex flex-col gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <div class="flex flex-col gap-1">
          <h3 class="m-0 text-base font-medium text-n-slate-12">
            {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.TITLE') }}
          </h3>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.SUBTITLE') }}
          </p>
        </div>
        <table v-if="confusionRows.length" class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">
                {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.COL_FROM') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.COL_TO') }}
              </th>
              <th class="py-2 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.COL_N') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in confusionRows"
              :key="`confusion-${index}`"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="py-2 pr-4">{{ stateLabel(row.from) }}</td>
              <td class="py-2 pr-4">{{ stateLabel(row.to) }}</td>
              <td class="py-2 text-right tabular-nums">
                {{ formatNumber(row.n) }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="m-0 text-sm text-n-slate-11">
          {{ $t('DEBT_STATE_GOVERNANCE.CONFUSION.EMPTY') }}
        </p>
      </div>

      <!-- Confidence band calibration -->
      <div
        class="flex flex-col gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <div class="flex flex-col gap-1">
          <h3 class="m-0 text-base font-medium text-n-slate-12">
            {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.TITLE') }}
          </h3>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.NOTE') }}
          </p>
        </div>
        <table v-if="calibrationRows.length" class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">
                {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.COL_BAND') }}
              </th>
              <th class="py-2 pr-4 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.COL_TOTAL') }}
              </th>
              <th class="py-2 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.COL_PCT') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in calibrationRows"
              :key="`calibration-${index}`"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="py-2 pr-4">{{ bandLabel(row.band) }}</td>
              <td class="py-2 pr-4 text-right tabular-nums">
                {{ formatNumber(row.total) }}
              </td>
              <td class="py-2 text-right tabular-nums">
                {{ formatPercent(row.pct_corrected) }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="m-0 text-sm text-n-slate-11">
          {{ $t('DEBT_STATE_GOVERNANCE.CALIBRATION.EMPTY') }}
        </p>
      </div>

      <!-- Daily adoption -->
      <div
        class="flex flex-col gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <h3 class="m-0 text-base font-medium text-n-slate-12">
          {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.TITLE') }}
        </h3>
        <table v-if="adoptionRows.length" class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">
                {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.COL_DAY') }}
              </th>
              <th class="py-2 pr-4 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.COL_FEEDBACKS') }}
              </th>
              <th class="py-2 pr-4 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.COL_AGENTS') }}
              </th>
              <th class="py-2 font-medium text-right">
                {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.COL_CONVERSATIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in adoptionRows"
              :key="`adoption-${index}`"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="py-2 pr-4">{{ row.day }}</td>
              <td class="py-2 pr-4 text-right tabular-nums">
                {{ formatNumber(row.feedbacks) }}
              </td>
              <td class="py-2 pr-4 text-right tabular-nums">
                {{ formatNumber(row.agents) }}
              </td>
              <td class="py-2 text-right tabular-nums">
                {{ formatNumber(row.conversations) }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="m-0 text-sm text-n-slate-11">
          {{ $t('DEBT_STATE_GOVERNANCE.ADOPTION.EMPTY') }}
        </p>
      </div>
    </template>
  </div>
</template>
